import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import { useTheme } from '../../../context/ThemeContext'

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
)

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
)

const CoordinatorFooter = () => (
  <footer className="border-t border-black/10 dark:border-white/10 px-6 py-8 mt-auto relative z-10">
    <div className="flex flex-col sm:flex-row justify-between gap-6">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <img src="/Mufulira_Wanderers_logo.png" alt="Logo" className="w-6 h-6 object-contain" />
          <span className="font-black text-gray-900 dark:text-white tracking-widest uppercase text-xs">MWFC</span>
        </div>
        <p className="text-gray-400 text-xs">Chapter Coordinator Portal</p>
      </div>
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Quick Links</div>
        <div className="flex flex-col gap-2">
          <Link to="/coordinator" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Overview</Link>
          <Link to="/coordinator/members" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Members</Link>
          <Link to="/admin/scan" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Scan Card</Link>
        </div>
      </div>
    </div>
    <div className="border-t border-black/10 dark:border-white/10 pt-6 mt-6">
      <p className="text-gray-400 text-xs uppercase tracking-widest">
        © {new Date().getFullYear()} MWFC
      </p>
    </div>
  </footer>
)

export default function CoordinatorLayout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { isDark, toggleTheme } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/staff/login')
  }

  const navItems = [
    { label: 'Overview', path: '/coordinator' },
    { label: 'Members', path: '/coordinator/members' },
    { label: 'Scan Card', path: '/admin/scan' },
  ]

  const isActive = (path) => location.pathname === path

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-black/10 dark:border-white/10">
        <div className="flex items-center gap-3">
          <img src="/Mufulira_Wanderers_logo.png" alt="Mufulira Wanderers" className="w-8 h-8 object-contain" />
          <div>
            <div className="font-black text-gray-900 dark:text-white tracking-widest uppercase text-sm">MWFC</div>
            <div className="text-green-500 text-xs font-bold uppercase tracking-widest">Chapter Coordinator</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 flex flex-col gap-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setSidebarOpen(false)}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-widest transition-colors ${
              isActive(item.path)
                ? 'bg-green-600 text-white'
                : 'text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
            }`}
          >
            {item.label}
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-black/10 dark:border-white/10 flex items-center justify-between">
        <button onClick={handleLogout} className="px-4 py-3 text-sm font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          Logout
        </button>
        <button onClick={toggleTheme} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>
      </div>
    </>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-white flex">

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-black border-b border-black/10 dark:border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/Mufulira_Wanderers_logo.png" alt="Mufulira Wanderers" className="w-7 h-7 object-contain" />
          <span className="font-black text-gray-900 dark:text-white tracking-widest uppercase text-sm">Coordinator</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          {sidebarOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Drawer Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Mobile Drawer */}
      <div className={`lg:hidden fixed top-0 left-0 h-full w-64 z-50 bg-white dark:bg-black border-r border-black/10 dark:border-white/10 flex flex-col transform transition-transform duration-200 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <SidebarContent />
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex w-64 bg-white dark:bg-black border-r border-black/10 dark:border-white/10 flex-col shrink-0">
        <SidebarContent />
      </div>

      {/* Main Content + Footer */}
      <div className="flex-1 flex flex-col min-w-0 mt-14 lg:mt-0 relative">
        {/* Watermark */}
        <div
          className="absolute inset-0 bg-center bg-no-repeat bg-contain opacity-[0.08] dark:opacity-[0.06] pointer-events-none z-0"
          style={{ backgroundImage: "url('/Mufulira_Wanderers_logo.png')" }}
        />
        <main className="flex-1 p-6 lg:p-10 overflow-y-auto relative z-10">
          {children}
        </main>
        <CoordinatorFooter />
      </div>
    </div>
  )
}